import {filter, isEmpty, map} from 'lodash';
import {useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, Accordion, AccordionTitle, ParticipantName, AccordionDetails} from 'components';
import {UserSessionContext} from 'context';
import {useUserSession} from 'hooks';
import {SessionScreenshotSubject} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {InviteeParticipantRole} from 'types';

import {ScreenshotPlaceholder} from '../../ScreenshotPlaceholder';
import {SidebarView} from '../../types';
import {SidebarContent} from '../SidebarContent';

const SESSION_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION;

const isClientRole = (role?: InviteeParticipantRole) => !role || role === InviteeParticipantRole.Client;

export const ScreenshotsSidebar: React.FC = () => {
    const {t} = useTranslation();
    const {
        screenshots, localParticipant, clientParticipant, guestParticipants,
        receivedScreenshots, isScreenshotsPreviewEnabled, canManageSession, guestEnabled
    } = useUserSession();
    const {screenshotsPreview, canMakeScreenshot, onScreenshotClick, onToggleScreenshotsPreview} = useContext(UserSessionContext);

    const screenScreenshots = useMemo(() => filter(screenshots, (s) =>
        s.subject === SessionScreenshotSubject.LocalScreen), [screenshots]);
    const clientScreenshots = useMemo(() => filter(screenshots, (s) =>
        s.subject === SessionScreenshotSubject.ParticipantVideo
        && (!guestEnabled || isClientRole(s.role))), [screenshots, guestEnabled]);
    const guestVideoScreenshots = useMemo(() => guestEnabled
        ? filter(screenshots, (s) => s.subject === SessionScreenshotSubject.ParticipantVideo
            && s.role === InviteeParticipantRole.Guest)
        : [], [screenshots, guestEnabled]);

    const receivedScreenScreenshotsCount = useMemo(() =>
        filter(screenScreenshots, (s) => s.received).length, [screenScreenshots]);
    const receivedClientScreenshotsCount = useMemo(() =>
        filter(clientScreenshots, (s) => s.received).length, [clientScreenshots]);
    const receivedGuestVideoScreenshotsCount = useMemo(() =>
        filter(guestVideoScreenshots, (s) => s.received).length, [guestVideoScreenshots]);

    return (
        <SidebarContent
            view={SidebarView.Screenshots}
            header={
                <div className='tw-flex tw-items-center tw-w-full'>
                    <div className='tw-ts-h5-bold tw-flex-1'>{t(SESSION_T_KEYS.LABELS.SCREENSHOTS)}</div>
                    {isScreenshotsPreviewEnabled && receivedScreenshots.length > 0 &&
                        <Icon name={screenshotsPreview ? 'eye-off' : 'eye'} button onClick={onToggleScreenshotsPreview} />}
                </div>
            }
        >
            {!isEmpty(screenScreenshots) &&
                <Accordion padding={false} shadow={false} defaultExpanded className='tw-my-2'>
                    <AccordionTitle iconPlacement='right' styled={false}>
                        <div className='tw-flex tw-items-center'>
                            <ParticipantName participant={localParticipant} local />
                            <div className='tw-ml-1'>{`(${receivedScreenScreenshotsCount}/${screenScreenshots.length})`}</div>
                        </div>
                    </AccordionTitle>
                    <AccordionDetails>
                        <div className='tw-grid tw-grid-cols-2 tw-justify-items-center tw-gap-y-6 tw-text-center tw-my-4'>
                            {map(screenScreenshots, (screenshot) => (
                                <ScreenshotPlaceholder
                                    key={screenshot.name}
                                    screenshot={screenshot}
                                    disabled={!canManageSession}
                                    onClick={onScreenshotClick}
                                />
                            ))}
                        </div>
                    </AccordionDetails>
                </Accordion>}
            {!isEmpty(clientScreenshots) && clientParticipant &&
                <Accordion padding={false} shadow={false} defaultExpanded className='tw-my-2'>
                    <AccordionTitle iconPlacement='right' styled={false}>
                        <div className='tw-flex tw-items-center'>
                            <ParticipantName participant={clientParticipant} />
                            <div className='tw-ml-1'>{`(${receivedClientScreenshotsCount}/${clientScreenshots.length})`}</div>
                        </div>
                    </AccordionTitle>
                    <AccordionDetails>
                        <div className='tw-grid tw-grid-cols-2 tw-justify-items-center tw-gap-y-6 tw-text-center tw-my-4'>
                            {map(clientScreenshots, (screenshot) => (
                                <ScreenshotPlaceholder
                                    key={screenshot.name}
                                    screenshot={screenshot}
                                    participantId={clientParticipant?.id}
                                    disabled={!canMakeScreenshot(screenshot.name) || !canManageSession}
                                    onClick={onScreenshotClick}
                                />
                            ))}
                        </div>
                    </AccordionDetails>
                </Accordion>}
            {!isEmpty(guestVideoScreenshots) && map(guestParticipants, (guest) => (
                <Accordion key={guest.id} padding={false} shadow={false} defaultExpanded className='tw-my-2'>
                    <AccordionTitle iconPlacement='right' styled={false}>
                        <div className='tw-flex tw-items-center'>
                            <ParticipantName participant={guest} />
                            <div className='tw-ml-1'>
                                {`(${receivedGuestVideoScreenshotsCount}/${guestVideoScreenshots.length})`}
                            </div>
                        </div>
                    </AccordionTitle>
                    <AccordionDetails>
                        <div className='tw-grid tw-grid-cols-2 tw-justify-items-center tw-gap-y-6 tw-text-center tw-my-4'>
                            {map(guestVideoScreenshots, (screenshot) => (
                                <ScreenshotPlaceholder
                                    key={screenshot.name}
                                    screenshot={screenshot}
                                    participantId={guest.id}
                                    disabled={!canMakeScreenshot(screenshot.name) || !canManageSession}
                                    onClick={onScreenshotClick}
                                />
                            ))}
                        </div>
                    </AccordionDetails>
                </Accordion>
            ))}
        </SidebarContent>
    );
};
